import React from "react";
import styled, { css } from "styled-components";
import { Link } from "@inertiajs/react";
import {
  airplay_video_src,
  book_src,
  hamburger_center_justify_src,
  laptop_macbook_src,
  library_src,
  mic_src,
  search_icon_src,
} from "../images/index";
import {
  narrowScreenMediaQuery,
  off_black,
  orangish,
  shaded_gray,
  outerContainerCss,
  taxsam_yellow_navbar,
  white,
} from "../constants/styles";
import Notification from "./Notification";
import UserProfileMenu from "./UserProfileMenu";
import { LandingSearchContext } from "../providers/ProvideLandingSearch";
import { LoggedInUserContext } from "../providers/ProvideLoggedInUser";
import { unmodule } from "../util";
import useHideOnScrollDown from "../hooks/useHideOnScrollDown";

// Controlled by the parent so the top-level Header can coordinate between the
// sidebar's open state and the auto-hide-on-scroll behavior — when the
// sidebar is open on narrow viewports we must keep the header visible so the
// close/burger button never disappears behind the overlay.
const NarrowSidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const { toggleSearchBox } = React.use(LandingSearchContext);
  const toggleSidebar = React.useCallback(() => {
    setSidebarOpen((open) => !open);
  }, [setSidebarOpen]);
  const handleEscapeClick = React.useCallback(
    (evt) => {
      if (evt.key === "Escape") {
        setSidebarOpen(false);
      }
    },
    [setSidebarOpen],
  );
  React.useEffect(() => {
    if (sidebarOpen) {
      document.addEventListener("keydown", handleEscapeClick);
      return () => {
        document.removeEventListener("keydown", handleEscapeClick);
      };
    } else {
      document.removeEventListener("keydown", handleEscapeClick);
    }
  }, [sidebarOpen, handleEscapeClick]);

  return (
    <React.Fragment>
      <BurgerButton
        type="button"
        $shownWhenNarrow
        onClick={toggleSidebar}
        aria-label={sidebarOpen ? "Tutup menu" : "Buka menu"}
        aria-expanded={sidebarOpen}
      >
        <BurgerIcon src={hamburger_center_justify_src} alt="" />
      </BurgerButton>
      {sidebarOpen && (
        <NarrowSidebarSection>
          <NarrowSidebarOverlay
            onClick={() => {
              setSidebarOpen(false);
            }}
          />
          <NarrowSidebarContainer role="dialog" aria-label="Menu navigasi">
            <SidebarLogo>
              <Link href="/">
                <img
                  src={unmodule(require("../images/taxsam-logo.png"))}
                  alt="logo taxsam"
                />
              </Link>
            </SidebarLogo>
            <SidebarMenuSeparator />

            <SidebarSearchButtonContainer>
              <SearchButton onClick={toggleSearchBox}>
                <SearchIcon src={search_icon_src} />
                <span>Cari</span>
              </SearchButton>
            </SidebarSearchButtonContainer>

            <SidebarMenuSeparator />
            <SidebarMenuTitle>LEARNING TAXSAM.CO</SidebarMenuTitle>
            <SidebarMenuSeparator />
            <SidebarSecondaryMenuContainer>
              <MenuWithIconLink href="/courses">
                <IconInLeftText src={airplay_video_src} />
                Video Pembelajaran
              </MenuWithIconLink>
              <MenuWithIconLink href="/talks">
                <IconInLeftText src={mic_src} />
                Podcast TaxsamTalks
              </MenuWithIconLink>
              <MenuWithIconLink
                href="https://taxbase.taxsam.co/library/workbook"
                target="_blank"
              >
                <IconInLeftText src={library_src} />
                Perpustakaan
              </MenuWithIconLink>
            </SidebarSecondaryMenuContainer>
            <SidebarMenuSeparator />
            <SidebarMenuTitle>TAXSAM.CO SERVICES</SidebarMenuTitle>
            <SidebarMenuSeparator />
            <SidebarSecondaryMenuContainer>
              <MenuWithIconLink
                href="https://taxbase.taxsam.co/library/workbook"
                target="_blank"
              >
                <IconInLeftText src={book_src} />
                Database Perpajakan
              </MenuWithIconLink>
              <MenuWithIconLink
                href="https://taxbase.taxsam.co/library/workbook"
                target="_blank"
              >
                <IconInLeftText src={laptop_macbook_src} />
                Sistem Informasi Akuntansi
              </MenuWithIconLink>
            </SidebarSecondaryMenuContainer>
          </NarrowSidebarContainer>
        </NarrowSidebarSection>
      )}
    </React.Fragment>
  );
};

const BurgerButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: none;
  justify-content: center;
  padding: 8px;
  transition: background 120ms ease;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  ${(props) =>
    props.$shownWhenNarrow &&
    css`
      @media ${narrowScreenMediaQuery} {
        display: inline-flex;
      }
    `};

  [data-theme="dark"] &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const BurgerIcon = styled.img`
  width: 24px;
  height: 24px;
  display: block;
  /* Source SVG has inline fill:white — tint to dark for light theme. */
  filter: brightness(0) saturate(100%);
  opacity: 0.8;

  [data-theme="dark"] & {
    filter: brightness(0) saturate(100%) invert(1);
  }
`;

const UnnamedComponent = (props) => {
  const $transparent = !!props.$transparent;
  const { toggleSearchBox, searchBox } = React.use(LandingSearchContext);
  const { signedIn, loading } = React.use(LoggedInUserContext);

  // Lifted from NarrowSidebar so the scroll hook can suppress auto-hide
  // while the mobile menu is open (otherwise the burger/close button would
  // vanish under the overlay).
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const hidden = useHideOnScrollDown({ disabled: sidebarOpen });

  return (
    <React.Fragment>
      {searchBox}
      <StickyHeader $hidden={hidden}>
        <HeaderSection $transparent={$transparent}>
          <HeaderFlex>
            <HeaderLeftPart>
              <HeaderLogoDiv>
                <Link href="/">
                  <HeaderIcon
                    src={unmodule(require("../images/taxsam-logo.png"))}
                  />
                </Link>
              </HeaderLogoDiv>
              <HeaderLeftPartMenuAfterLogo>
                <MenuLink $active href="#">
                  TLC
                </MenuLink>
                <MenuLink href="https://taxbase.taxsam.co" target="_blank">
                  Taxbase
                </MenuLink>
                <MenuLink href="#">Sistem Informasi Akuntansi</MenuLink>
                <MenuLink href="/coming_soon?locale=id">
                  Case Management System
                </MenuLink>
              </HeaderLeftPartMenuAfterLogo>
            </HeaderLeftPart>
            <HeaderRightPart>
              <SearchButton onClick={toggleSearchBox}>
                <SearchIcon src={search_icon_src} />
                <span>Cari</span>
              </SearchButton>
              {!loading && !signedIn ? (
                <LoginPillButton href="/login">
                  Masuk
                </LoginPillButton>
              ) : (
                <>
                  <Notification />
                  <UserProfileMenu />
                </>
              )}
              <NarrowSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
            </HeaderRightPart>
          </HeaderFlex>
        </HeaderSection>
        <HeaderSectionSecondary $transparent={$transparent}>
          <SecondaryHeaderFlex>
            <MenuLink href="#sertifikasi">Sertifikasi</MenuLink>
            <MenuLink href="#rekaman">Video Pembelajaran</MenuLink>
            <MenuLink href="#talksamtalk">Podcast TaxsamTalks</MenuLink>
            <MenuLink
              href="https://taxbase.taxsam.co/library/workbook"
              target="_blank"
              rel="noreferrer noopener"
            >
              Perpustakaan
            </MenuLink>
          </SecondaryHeaderFlex>
        </HeaderSectionSecondary>
      </StickyHeader>
    </React.Fragment>
  );
};
export default UnnamedComponent;

// Inertia Link so clicking Masuk swaps the SPA page instead of triggering
// a full reload. Inertia transparently falls back to a full navigation if
// the server returns a non-Inertia response (covers the legacy toggle-OFF
// branch where /login redirects to the homepage popup).
const LoginPillButton = styled(Link)`
  align-items: center;
  background: linear-gradient(90deg, #d85907 0%, #f7b30c 100%);
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(216, 89, 7, 0.25);
  color: #ffffff;
  display: inline-flex;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  padding: 10px 28px;
  text-decoration: none;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;

  &:hover {
    box-shadow: 0 6px 16px rgba(216, 89, 7, 0.35);
    color: #ffffff;
    filter: brightness(1.05);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// $hidden animates the header off-screen on scroll-down and back on
// scroll-up. position: sticky still functions while transformed — CSS
// transforms apply after positioning, so the element remains sticky at
// top: 0 in layout while visually translated away. will-change: transform
// hints the compositor to promote this to its own layer for smooth 60fps
// hide/show without repaints.
export const StickyHeader = styled.div`
  left: 0;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 30;
  transform: translateY(${({ $hidden }) => ($hidden ? "-100%" : "0")});
  transition: transform 280ms cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const HeaderSection = styled.div`
  background-color: #fef5e0eb;
  backdrop-filter: blur(6px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);

  ${(props) =>
    props.$transparent &&
    css`
      background-color: unset;
      position: relative;

      &:before {
        content: " ";
        backdrop-filter: blur(6px);
        background-color: #fef5e0eb;
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
        z-index: -1;
      }
    `};

  &[data-theme="dark"], [data-theme="dark"] & {
    background-color: rgba(23, 23, 30, 0.85);
    border-bottom-color: rgba(255, 255, 255, 0.08);
  }
`;

const HeaderSectionSecondary = styled(HeaderSection)`
  background-color: ${white};
  backdrop-filter: none;

  &:after {
    background: linear-gradient(
      90deg,
      rgb(255, 107, 0) 0%,
      rgb(255, 140, 0) 16.67%,
      rgb(255, 165, 0) 33.33%,
      rgb(74, 144, 226) 50%,
      rgb(53, 122, 189) 66.67%,
      rgb(37, 99, 235) 83.33%,
      rgb(29, 78, 216) 100%
    );
    content: "";
    display: block;
    height: 4px;
    width: 100%;
  }

  ${(props) =>
    props.$transparent &&
    css`
      &:before {
        background-color: #ffffffeb;
      }
    `};

  &[data-theme="dark"], [data-theme="dark"] & {
    background-color: #17171e;
  }
`;

const HeaderFlex = styled.div`
  ${outerContainerCss};
  align-items: center;
  border-color: #353333;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: space-between;
  padding-bottom: 0.75rem;
  padding-top: 0.75rem;
`;

const SecondaryHeaderFlex = styled(HeaderFlex)`
  gap: 0;
  justify-content: start;
  padding-bottom: 0;
  padding-top: 0;

  @media ${narrowScreenMediaQuery} {
    display: none;
  }
`;

// The header/search button feature is intentionally hidden on the
// rebranded homepage while the search experience is being redesigned
// (the existing search box still mounts from LandingSearchContext when
// other pages need it). Keeping the styled component around lets us flip
// `display: none` back to `display: flex` when the new design lands —
// without scattering the old CSS back through the codebase.
const SearchButton = styled.button`
  align-items: center;
  background-color: #2f2f2f;
  border-radius: 7px;
  color: ${white};
  cursor: pointer;
  display: none;
  line-height: 1.5rem;
  max-height: 38px;
  outline: none;
  padding: 5px 25px 5px 35px;
  position: relative;
`;

const SearchIcon = styled.img`
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const HeaderPart = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
`;

const HeaderLeftPart = styled(HeaderPart)`
  flex: 3 1 0;
`;

const HeaderRightPart = styled(HeaderPart)`
  flex: 1 1 0;
  justify-content: end;

  @media ${narrowScreenMediaQuery} {
    & > ${SearchButton} {
      display: none;
    }
  }
`;

const HeaderLogoDiv = styled.div`
  max-width: 154px;
`;

const HeaderLeftPartMenuAfterLogo = styled.div`
  align-items: center;
  border-left: 1px solid ${shaded_gray};
  display: flex;
  gap: 16px;

  @media ${narrowScreenMediaQuery} {
    display: none;
  }
`;

const MenuLink = styled.a`
  color: ${off_black};
  font-size: 16px;
  font-weight: 500;
  text-decoration: none;
  border-radius: 6px;

  &:hover {
    color: ${orangish};
  }

  &:focus-visible {
    outline: 2px solid ${orangish};
    outline-offset: 2px;
  }

  &:first-child {
    padding-left: 10px;
  }

  ${HeaderSectionSecondary} & {
    font-size: 14px;
    padding: 16px 16px;

    &:hover {
      background-color: #fff7ed;
    }
  }

  ${(props) =>
    props.$active &&
    css`
      color: ${orangish};
    `};

  [data-theme="dark"] & {
    color: #f3f4f6;

    ${HeaderSectionSecondary} & {
      &:hover {
        background-color: rgba(255, 255, 255, 0.06);
      }
    }
  }
`;

const MenuWithIconLink = styled(MenuLink)`
  align-items: center;
  display: flex;
  gap: 10px;
`;

const HeaderIcon = styled.img`
  width: 100%;
`;

const NarrowSidebarSection = styled.div`
  display: none;

  @media ${narrowScreenMediaQuery} {
    display: block;
  }
`;

const NarrowSidebarOverlay = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  height: 100vh;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  animation: fadeIn 200ms ease;
  z-index: 20;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const NarrowSidebarContainer = styled.div`
  --narrow-sidebar-width: 280px;
  background-color: #ffffff;
  border-right: 1px solid #e5e7eb;
  color: #1f2937;
  height: 100vh;
  left: 0;
  padding: 24px 0;
  position: fixed;
  top: 0;
  transform: translateX(0);
  transition: transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
  width: var(--narrow-sidebar-width);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  z-index: 21;
  animation: slideIn 220ms cubic-bezier(0.22, 1, 0.36, 1);
  overflow-y: auto;

  @keyframes slideIn {
    from { transform: translateX(-100%); }
    to { transform: translateX(0); }
  }

  [data-theme="dark"] & {
    background-color: #17171e;
    border-right-color: rgba(255, 255, 255, 0.08);
    color: #f3f4f6;
  }
`;

const SidebarLogo = styled.div`
  margin: 0 20px;

  > a {
    display: inline-block;

    & > img {
      max-width: 150px;
    }
  }
`;

const SidebarMenuSeparator = styled.div`
  background-color: #e5e7eb;
  height: 1px;
  margin: 10px 0;

  [data-theme="dark"] & {
    background-color: rgba(255, 255, 255, 0.08);
  }
`;

const SidebarMenuTitle = styled.h4`
  color: #9ca3af;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-left: 20px;
  margin-right: 20px;
  text-transform: uppercase;

  [data-theme="dark"] & {
    color: #6b7280;
  }
`;

const SidebarSecondaryMenuContainer = styled.div`
  margin: 8px 12px;

  & > ${MenuLink} {
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 14px;
    font-weight: 500;

    &:hover {
      background: #fff7ed;
      color: #d85907;
    }

    [data-theme="dark"] &:hover {
      background: rgba(216, 89, 7, 0.15);
      color: #fdba74;
    }
  }
`;

const IconInLeftText = styled.img`
  display: inline-block;
  width: 18px;
  height: 18px;
  /* SVG icons have hardcoded white fill — tint to dark for light theme. */
  filter: brightness(0) saturate(100%);
  opacity: 0.7;

  [data-theme="dark"] & {
    filter: brightness(0) saturate(100%) invert(1);
    opacity: 0.9;
  }
`;

const SidebarSearchButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;
